import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { CardTable } from 'components/datatable/NewTable'
import NumberField from '../../../components/forms/NumberField'
import Select from '../../../components/forms/Select'
import { nbPerPageLabelShort } from '../../../referencial/constants/ReferencialConstants'
import DtoParametrageDataType from '../../../piezometry/dto/DtoParametrageDataType'
import { hasValue } from '../../../utils/NumberUtil'
import DtoMeasureStats from '../../../station/dto/piezometer/DtoMeasureStats'
import { darkBlue, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import { isArray, isEmpty, isNil, uniq, uniqBy } from 'lodash'
import { Grid, Icon, Tooltip } from '@mui/material'
import IAEauAction from '../../../iaeau/IAEauAction'

const DEFAULT_PREVISION_SERIE = 'Prévision'
const SEEDS_SOURCE = 'seeds'
const LOADING = 'LOADING'

const getStatsKey = (id, typeId) => `${id}_${typeId}`
const getSerieKey = (id, typeId, predSource) => `${id}_${typeId}_${predSource}`
const isSeedsSource = predSource => predSource.toLowerCase().includes(SEEDS_SOURCE)
const getSourceOptions = stats => uniqBy(stats, 'source').map(s => ({ value: s.source, label: s.source }))
const getDefaultSerieOptions = () => [{ value: DEFAULT_PREVISION_SERIE, label: i18n.prevision }]

const TabSelectThreshold = ({
    stations = [],
    dataTypes = [],
    thresholds = [],
    stationsSelected = [],
    onChangeStation = () => {},
    onChange = () => {},
    onDeleteStation = () => {},
    onDuplicateStation = () => {},
    disabled = true,
    actions= [],
    stationType = 'hydrometry',
    stationsStats = {},
    modePrevisionnel = false,
}) => {
    const [predStats, setPredStats] = useState({})
    const [predSeries, setPredSeries] = useState({})

    const loadPredStats = (id, typeId, key) => {
        setPredStats(prev => ({ ...prev, [key]: LOADING }))
        IAEauAction.promisePredStats(stationType, id).then(stats => {
            setPredStats(prev => ({ ...prev, [key]: (stats ?? []).filter(s => s.typeId === typeId) }))
        })
    }

    const loadPredSeries = (id, predSource, key, stats) => {
        const stat = stats.find(s => s.source === predSource)
        if (isNil(stat)) {
            setPredSeries(prev => ({ ...prev, [key]: [] }))
        } else {
            setPredSeries(prev => ({ ...prev, [key]: LOADING }))
            IAEauAction.promiseModelMeasures(stationType, id, stat.idModel, stat.source, stat.maxSimulationDate, undefined, stat.typeId).then(measures => {
                const namedSeries = uniq((measures ?? []).map(m => m.serieName).filter(s => s && s !== 'undefined'))
                const hasDefaultSerie = (measures ?? []).some(m => !m.serieName || m.serieName === 'undefined')
                const options = [
                    ...(hasDefaultSerie ? getDefaultSerieOptions() : []),
                    ...namedSeries.map(s => ({ value: s, label: s })),
                ]
                setPredSeries(prev => ({ ...prev, [key]: options }))
            })
        }
    }

    useEffect(() => {
        if (!modePrevisionnel) {
            return
        }
        const filledRows = stationsSelected.filter(({ id, typeId }) => hasValue(id) && hasValue(typeId))

        uniqBy(filledRows.map(({ id, typeId }) => ({ id, typeId, key: getStatsKey(id, typeId) })), 'key')
            .filter(({ key }) => isNil(predStats[key]))
            .forEach(({ id, typeId, key }) => loadPredStats(id, typeId, key))

        uniqBy(filledRows.filter(({ predSource }) => !!predSource)
            .map(({ id, typeId, predSource }) => ({ id, typeId, predSource, key: getSerieKey(id, typeId, predSource) })), 'key')
            .filter(({ id, typeId, key }) => isNil(predSeries[key]) && isArray(predStats[getStatsKey(id, typeId)]))
            .forEach(({ id, typeId, predSource, key }) => {
                if (isSeedsSource(predSource)) {
                    // source "seeds" : la seule série exploitable est la prévision (médiane des tirages, calculée côté back)
                    setPredSeries(prev => ({ ...prev, [key]: getDefaultSerieOptions() }))
                } else {
                    loadPredSeries(id, predSource, key, predStats[getStatsKey(id, typeId)])
                }
            })
    }, [modePrevisionnel, stationsSelected, predStats, predSeries])

    useEffect(() => {
        if (!modePrevisionnel || disabled) {
            return
        }
        stationsSelected.forEach(({ id, typeId, predSource, predSerie }, index) => {
            const stats = predStats[getStatsKey(id, typeId)]
            if (isNil(predSource)) {
                const sourceOptions = isArray(stats) ? getSourceOptions(stats) : []
                if (sourceOptions.length === 1) {
                    onChange('predSource', sourceOptions[0].value, index)
                }
            } else if (isNil(predSerie)) {
                const serieOptions = predSeries[getSerieKey(id, typeId, predSource)]
                if (isArray(serieOptions) && serieOptions.length === 1) {
                    onChange('predSerie', serieOptions[0].value, index)
                }
            }
        })
    }, [modePrevisionnel, disabled, stationsSelected, predStats, predSeries])

    const getLoadingIcon = () => (
        <Tooltip title={i18n.loading}>
            <Icon
                sx={{
                    color: 'action.active',
                    animation: 'predLoadingSpin 1.2s linear infinite',
                    '@keyframes predLoadingSpin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                    },
                }}
            >
                autorenew
            </Icon>
        </Tooltip>
    )

    const getPredCell = (options, value, onChangeValue) => {
        if (isNil(options)) {
            return undefined
        }
        if (options === LOADING) {
            return getLoadingIcon()
        }
        if (isEmpty(options)) {
            return i18n.noDataAvailable
        }
        return (
            <Select
                options={options}
                value={value}
                onChange={onChangeValue}
                keyValue='value'
                keyLabel='label'
                disabled={disabled}
            />
        )
    }

    const getPredSourceCell = (id, predSource, typeId, index) => {
        const stats = predStats[getStatsKey(id, typeId)]
        const options = isArray(stats) ? getSourceOptions(stats) : stats
        return getPredCell(options, predSource, v => onChange('predSource', v, index))
    }

    const getPredSerieCell = (id, predSource, predSerie, typeId, index) => {
        const options = predSource ? predSeries[getSerieKey(id, typeId, predSource)] : undefined
        return getPredCell(options, predSerie, v => onChange('predSerie', v, index))
    }

    const getThresholdDisplay = (thresholdId, thresholdList, index) => {
        if (isEmpty(thresholdList)) {
            return (
                <Grid container justifyContent='flex-start' spacing={1}>
                    {i18n.noThresholds}
                    {
                        !isNil(thresholdId) && (
                            <Tooltip title={i18n.thresholdSelectedNoExists}>
                                <Icon
                                    sx={{ color: PASTEL_COLOR.red, cursor: !disabled && 'pointer' }}
                                    onClick={() => {
                                        if (!disabled) onChange('thresholdId', undefined, index)
                                    }}
                                >
                                    warning_amber
                                </Icon>
                            </Tooltip>
                        )
                    }
                </Grid>
            )
        }
        return (
            <Grid container alignItems='center' spacing={2}>
                <Select
                    options={thresholdList}
                    value={thresholdId}
                    onChange={v => onChange('thresholdId', v, index)}
                    keyLabel={'title'}
                    keyValue={'id'}
                    nullLabel='&nbsp;'
                    disabled={disabled}
                    style={{ minWidth: '250px' }}
                />
                {
                    thresholdId && !thresholdList.some(th => th.id === thresholdId) && (
                        <Tooltip title={i18n.thresholdSelectedNoExistsChooseAnother}>
                            <Icon sx={{ color: PASTEL_COLOR.red }}>warning_amber</Icon>
                        </Tooltip>
                    )
                }
            </Grid>
        )
    }

    const getLine = (id, thresholdId, typeId, timeLinitMissingData, predSource, predSerie, thresholdList, index, allLines, code) => ({
        station: {
            value: (
                <Select
                    options={stations}
                    onChange={(_, value) => onChangeStation(value, index)}
                    value={id}
                    keyValue='id'
                    clearFunction
                    disabled={disabled}
                    obligatory
                />
            ),
            sortValue: code,
        },
        dataType: {
            value: (
                <Select
                    options={((id && stationsStats[id]) ?? dataTypes)}
                    onChange={v => onChange('typeId', v, index)}
                    value={typeId}
                    keyValue='id'
                    keyLabel={'title'}
                    disabled={disabled}
                    obligatory
                />
            ),
            sortValue: typeId,
        },
        predSource: {
            value: getPredSourceCell(id, predSource, typeId, index),
            sortValue: predSource,
        },
        predSerie: {
            value: getPredSerieCell(id, predSource, predSerie, typeId, index),
            sortValue: predSerie,
        },
        threshold: {
            value: getThresholdDisplay(thresholdId, thresholdList, index),
        },
        timeLinitMissingData: {
            value: (
                <NumberField
                    min={1}
                    value={timeLinitMissingData}
                    onChange={v => onChange('timeLinitMissingData', v, index)}
                    disabled={disabled}
                />
            ),
            sortValue: timeLinitMissingData,
        },
        color: allLines.filter(s => s.id === id && s.thresholdId === thresholdId && s.typeId === typeId).length >= 2 ? 'red' : undefined,
    })

    const rows = useMemo(() => stationsSelected.map(({ id, code, thresholdId, typeId, timeLinitMissingData, predSource, predSerie }, i) => {
        const thresholdList = (() => {
            if (stationType === 'hydrometry') {
                return thresholds.filter(t => id?.toString() === t.stationId)
            }
            if (code) {
                return thresholds.filter(t => t.code && t.code.toLowerCase() === code.toLowerCase())
            }
            return []
        })()
        const thresholdListWithTypeId = hasValue(typeId) ? thresholdList.filter(t => t.dataType === typeId.toString()) : thresholdList
        return getLine(id, thresholdId, typeId, timeLinitMissingData, predSource, predSerie, thresholdListWithTypeId, i, stationsSelected, code)
    }), [stationsSelected, stationType, thresholds, modePrevisionnel, predStats, predSeries, stations, stationsStats, disabled])

    const headers = modePrevisionnel
        ? ['station', 'dataType',
            { key: 'predSource', value: i18n.previsionSource },
            { key: 'predSerie', value: i18n.previsionSerie },
            'threshold', 'timeLinitMissingData']
        : ['station', 'dataType', 'threshold', 'timeLinitMissingData']

    return (
        <CardTable
            title={i18n.stations}
            rows={rows}
            headers={headers}
            rowsPerPageOptions={nbPerPageLabelShort}
            deletable={!disabled}
            onDelete={onDeleteStation}
            duplicable={!disabled}
            onDuplicate={onDuplicateStation}
            color={darkBlue}
            actions={actions}
            lineActions={[{
                icon: 'delete',
                onClick: onDeleteStation,
                tooltip: i18n.delete,
                displayed: !disabled,
            }, {
                icon: 'add_to_photos',
                onClick: onDuplicateStation,
                tooltip: i18n.duplicate,
                displayed: !disabled,
            } ]}
        />
    )
}

TabSelectThreshold.propTypes = {
    stationType: PropTypes.string,
    dataTypes: PropTypes.arrayOf(PropTypes.instanceOf(DtoParametrageDataType)),
    stations: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        name: PropTypes.string,
    })),
    thresholds: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        title: PropTypes.string,
    })),
    stationsSelected: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        name: PropTypes.string,
    })),
    onChangeStation: PropTypes.func,
    onChange: PropTypes.func,
    onDeleteStation: PropTypes.func,
    onDuplicateStation: PropTypes.func,
    disabled: PropTypes.bool,
    modePrevisionnel: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.shape({
        href: PropTypes.string,
        onClick: PropTypes.func,
        color: PropTypes.string,
        iconName: PropTypes.string.isRequired,
        tooltip: PropTypes.string,
    })),
    stationsStats: PropTypes.objectOf(PropTypes.arrayOf(DtoMeasureStats)),
}

export default TabSelectThreshold